import React, { MouseEventHandler, TouchEventHandler, useEffect, useRef, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PitConfType } from 'types/machineConf/machineConfType';
import { to2DArrayPit, toPitConfFrom2DArray } from 'utils/machineConf.helper';
import CLSFCN from 'constants/classification';
import pitMarkIcon from 'assets/images/pitMarkIcon.svg';
import pitCrossIcon from 'assets/images/pitCrossIcon.svg';

type Props = {
  pitConf: PitConfType;
  onChange: (newPitConf: PitConfType) => void;
  className?: string;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    userSelect: 'none',
    touchAction: 'none',
    '& *': {
      touchAction: 'none',
    },
  },
  container: {
    width: '100%',
    height: '100%',
  },
  table: {
    width: '100%',
    height: '100%',
    borderCollapse: 'collapse',
  },
  table9: {
    '& tr:nth-child(odd)': {
      borderTop: `2px solid ${theme.base.tableBorder}`,
      borderBottom: `2px solid ${theme.base.tableBorder}`,
    },
    '& tr:nth-child(even)': {
      borderTop: `2px solid ${theme.base.tableBorder}`,
      borderBottom: `2px solid ${theme.base.tableBorder}`,
    },
  },
  table36: {
    '& tr:nth-child(odd)': {
      borderTop: `2px solid ${theme.base.tableBorder}`,
      borderBottom: `2px dashed ${theme.base.tableBorder}`,
    },
    '& tr:nth-child(even)': {
      borderTop: `2px dashed ${theme.base.tableBorder}`,
      borderBottom: `2px solid ${theme.base.tableBorder}`,
    },
  },
  tableRow9: {
    height: '33.34%',
    '& td:nth-child(odd)': {
      borderLeft: `2px solid ${theme.base.tableBorder}`,
      borderRight: `2px solid ${theme.base.tableBorder}`,
    },
    '& td:nth-child(even)': {
      borderLeft: `2px solid ${theme.base.tableBorder}`,
      borderRight: `2px solid ${theme.base.tableBorder}`,
    },
  },
  tableRow36: {
    height: '16.67%',

    '& td:nth-child(odd)': {
      borderLeft: `2px solid ${theme.base.tableBorder}`,
      borderRight: `2px dashed ${theme.base.tableBorder}`,
    },
    '& td:nth-child(even)': {
      borderLeft: `2px dashed ${theme.base.tableBorder}`,
      borderRight: `2px solid ${theme.base.tableBorder}`,
    },
  },
  tableCell: {
    padding: '0',
    width: '33.34%',
  },
  tableCell2: {
    padding: '0',
    width: '16.67%',
  },
  pitBlock: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pitBlock9: {
    padding: '16px',
  },
  pitBlock36: {
    padding: '8px',
  },
  pitIcon: {
    pointerEvents: 'none',
  },
}));

// TouchMove中はこのコンポーネント内部のステートを切り替えるのみとする
// TouchEnd時にonChangeを介して渡された基のステートを更新する

const PitPanel: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { pitConf, onChange, className } = props;
  const { classes } = useStyles();
  const [pits, setPits] = useState(to2DArrayPit(pitConf));
  const inputMode = useRef<'0' | '1' | null>(null);
  const mouseEventSkip = useRef<boolean>(false);

  useEffect(() => {
    setPits(to2DArrayPit(pitConf));
  }, [pitConf.settingPattern]);

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (mouseEventSkip.current) return;
    inputMode.current = null;
    updatePanel(event.clientX, event.clientY);
  };

  const handleTouchStart: TouchEventHandler<HTMLDivElement> = (event) => {
    mouseEventSkip.current = true;
    // ※TouchMoveイベントは指を離すまで一番最初にタッチしたコンポーネントに対して発生し続ける
    inputMode.current = null;
    handleTouchMove(event);
  };

  const handleTouchMove: TouchEventHandler<HTMLDivElement> = (event) => {
    // ※TouchMoveイベントは指を離すまで一番最初にタッチしたコンポーネントに対して発生し続ける
    const touch = event.changedTouches[0];
    updatePanel(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    // ※PCとモバイルでイベントの発生仕方が異なる
    //   PC：マウスダウンした要素に関係なく、単純にマウスアップした要素でイベントが発生する→MouseUpだけでは編集終了を捕捉できないのでMouseLeaveも追加
    //   モバイル：要素内でタッチ開始すれば要素外でタッチ終了してもタッチ開始した要素でTouchEndイベントが発生する
    inputMode.current = null;
    if (pitConf) {
      const temp = toPitConfFrom2DArray(pitConf, pits);
      onChange(temp);
    }
  };

  const updatePanel = (clientX: number, clientY: number) => {
    // タッチしている落とし穴を取得
    const element = document.elementFromPoint(clientX, clientY) as HTMLDivElement;
    if (element && element.dataset.value) {
      // 最初にタッチした落とし穴の値を反転した値を入力モードとして設定
      if (inputMode.current == null) {
        inputMode.current = element.dataset.value === '1' ? '0' : '1';
      }

      // 現在の入力モードとタッチしている落とし穴の値が違ったら落とし穴の値を変更
      const x = Number(element.dataset.x);
      const y = Number(element.dataset.y);
      if (inputMode.current !== pits[y][x]) {
        const newone = pits.map((row) => row.slice());
        newone[y][x] = inputMode.current;
        setPits(newone);
      }
    }
  };

  return (
    <Box
      className={`${classes.root} ${className}`}
      onMouseDown={handleClick}
      onMouseLeave={handleTouchEnd}
      onMouseUp={handleTouchEnd}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}>
      <TableContainer className={classes.container}>
        <Table
          className={`${classes.table} ${
            pitConf.settingPattern === CLSFCN.PIT_CONFIG.PATTERN.DETAIL ? classes.table36 : classes.table9
          }`}>
          <TableBody>
            {pits.map((row, i) => (
              <TableRow
                className={`${
                  pitConf.settingPattern === CLSFCN.PIT_CONFIG.PATTERN.DETAIL ? classes.tableRow36 : classes.tableRow9
                }`}
                // eslint-disable-next-line react/no-array-index-key
                key={i}>
                {row.map((value, j) => (
                  <TableCell
                    className={
                      pitConf.settingPattern === CLSFCN.PIT_CONFIG.PATTERN.DETAIL
                        ? classes.tableCell2
                        : classes.tableCell
                    }
                    // eslint-disable-next-line react/no-array-index-key
                    key={j}>
                    <Box
                      data-x={j}
                      data-y={i}
                      data-value={value}
                      className={`${
                        pitConf.settingPattern === CLSFCN.PIT_CONFIG.PATTERN.DETAIL
                          ? classes.pitBlock36
                          : classes.pitBlock9
                      } ${classes.pitBlock}`}>
                      {value === '1' && <img src={pitMarkIcon} alt="○" className={classes.pitIcon} />}
                      {value !== '1' && <img src={pitCrossIcon} alt="×" className={classes.pitIcon} />}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PitPanel;
